/** Send recipient-bound draft invitations; show per-number results and retain a
 * request ID across retries so a network failure cannot silently send twice.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { normaliseTestNumber } from './testPhoneNumbers';
import { v4 as uuidv4 } from 'uuid';
import { Button, Modal, Label, Table } from 'semantic-ui-react';

const messages = {
  test_questionnaire_empty: 'Add sections and questions before sending test invitations.',
  flows_disabled: 'Flows was disabled. Refresh the questionnaire before sending invitations.',
  immutable_package_requires_operator_check:
    'The draft package needs an administrator check. No invitations were sent.',
  test_questionnaire_changed:
    'The questionnaire changed after this invitation was prepared. Send a new invitation.',
  test_draft_not_ready: 'Generate and validate a draft before inviting testers.',
  test_draft_changed: 'The questionnaire has changed. Generate its draft again before testing.',
  translation_not_ready: 'Wait for translation to finish, then generate the draft again.',
  invalid_test_numbers:
    'Enter up to 10 unique numbers, including country codes, separated by commas or new lines.',
  test_invitations_disabled: 'Test invitations are not enabled in this environment yet.',
  forbidden: 'Only Wakamoso administrators can send test invitations.',
  test_operation_requires_check: 'This operation needs an administrator check before sending again.',
  test_queue_uncertain: 'The request may have started. Check its status before sending again.',
};
const outcomes = {
  busy: 'An existing questionnaire or invitation is still open. Finish or cancel it before testing.',
  delivery_uncertain: 'Delivery needs an administrator check. Do not resend yet.',
};
export default function FlowTestPanel({ questionnaire, token, ready, inline = false, beforeOperation }) {
  const [open, setOpen] = useState(false);
  const [numbers, setNumbers] = useState('');
  const [result, setResult] = useState(null);
  const [submittedNumbers, setSubmittedNumbers] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const requestId = useRef(null);
  const started = useRef(0);
  const inFlight = useRef(false);
  // Only confirmed completion or a known pre-send failure permits a new batch.
  const canStartNew =
    !error &&
    ((result?.status === 'finished' &&
      result.outcomes?.every((row) => ['invited', 'busy'].includes(row.status)) &&
      submittedNumbers.every((number) =>
        result.outcomes?.some((row) => row.userId === number && ['invited', 'busy'].includes(row.status)),
      )) ||
      (result?.status === 'failed' &&
        !result.outcomes?.length &&
        [
          'test_draft_changed',
          'test_draft_not_ready',
          'test_questionnaire_changed',
          'test_questionnaire_empty',
          'flows_disabled',
          'translation_not_ready',
          'immutable_package_requires_operator_check',
        ].includes(result.error)));
  async function request(method) {
    if (inFlight.current) return;
    if (method === 'post' && requestId.current && !canStartNew) return;
    inFlight.current = true;
    setBusy(true);
    try {
      // Do not create an invitation ID or show Sending until save/discard succeeds.
      const saved =
        method === 'post' && beforeOperation ? await beforeOperation('Send invitations') : questionnaire;
      if (!saved) return;
      if (method === 'post') setSubmittedNumbers(recipients);
      if (method === 'post' && canStartNew) {
        requestId.current = null;
        setResult(null);
      }
      setError('');
      if (!requestId.current) {
        requestId.current = uuidv4();
        started.current = Date.now();
      }
      const response = await axios({
        method,
        url: `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_ENV}/whatsapp-flows/test-invitations`,
        params: {
          tenantId: saved.tenantId,
          questionnaireId: saved.id,
          ...(method === 'get' ? { requestId: requestId.current } : {}),
        },
        headers: { Authorization: `Bearer ${token}` },
        ...(method === 'post'
          ? {
              data: {
                requestId: requestId.current,
                numbers: recipients,
              },
            }
          : {}),
      });
      setResult(response.data);
    } catch (e) {
      if (method === 'post' && e.response?.status === 400) requestId.current = null;
      setError(
        messages[e.response?.data?.error] ||
          'Could not confirm this request. Check its status before sending again.',
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  useEffect(() => {
    if (!['queued', 'sending'].includes(result?.status) || Date.now() - started.current > 300000)
      return undefined;
    const timer = setTimeout(() => request('get'), 5000);
    return () => clearTimeout(timer);
    // Poll the currently selected request, without posting another invitation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);
  const entered = numbers
    .split(/[,\n]/)
    .map((n) => n.trim())
    .filter(Boolean);
  const normalised = entered.map(normaliseTestNumber);
  const recipients = [...new Set(normalised.filter(Boolean))];
  const rows = entered.map((raw, index) => ({
    raw,
    number: normalised[index],
    ignored: !normalised[index]
      ? 'Invalid number.'
      : normalised.indexOf(normalised[index]) !== index
        ? 'Duplicate number.'
        : null,
  }));
  const tooMany = entered.length > 10;
  const valid = recipients.length > 0 && !tooMany;
  const pending = busy || ['queued', 'sending'].includes(result?.status);
  // Keep numbers locked while a batch is pending or its delivery is uncertain.
  const content = (
    <section aria-label="Test questionnaire on WhatsApp" className="ui form">
      <div className={`field flow-test-number-field${tooMany ? ' error' : ''}`}>
        <label htmlFor="flow-test-numbers">WhatsApp numbers</label>
        <textarea
          id="flow-test-numbers"
          value={numbers}
          rows={2}
          disabled={pending || (Boolean(requestId.current) && !canStartNew)}
          aria-invalid={tooMany}
          aria-describedby={
            tooMany ? 'flow-test-number-help flow-test-number-error' : 'flow-test-number-help'
          }
          onChange={(e) => setNumbers(e.target.value)}
        />
        <small id="flow-test-number-help" className="flow-field-help">
          Enter up to 10 numbers, separated by commas or new lines. Use 082 192 0390 for South Africa, or an
          international number such as +27 82 192 0390.
        </small>
        {tooMany && (
          <span id="flow-test-number-error" className="validation-error" role="alert">
            Enter no more than 10 numbers.
          </span>
        )}
      </div>
      <h4 id="flow-invitations-heading" hidden={!entered.length}>
        Invitations
      </h4>
      {!!rows.length && (
        <Table
          compact
          unstackable
          className="flow-results-table"
          aria-label="Invitation recipients"
          aria-describedby="flow-invitations-heading"
          aria-live="polite"
        >
          <Table.Body>
            {rows.map(({ raw, number, ignored }, index) => {
              const submitted = submittedNumbers.includes(number);
              const outcome = submitted && result?.outcomes?.find((row) => row.userId === number);
              const status = ignored
                ? 'ignored'
                : !submitted
                  ? 'unsent'
                  : outcome?.status ||
                    (error
                      ? 'unknown'
                      : result?.status === 'failed'
                        ? 'failed'
                        : result?.status === 'finished'
                          ? 'unknown'
                          : 'sending');
              const label =
                {
                  ignored: 'Ignored',
                  unsent: 'Unsent',
                  invited: 'Sent',
                  busy: 'Not sent',
                  failed: 'Failed',
                  sending: 'Sending',
                  delivery_uncertain: 'Needs checking',
                  unknown: 'Needs checking',
                }[status] || 'Needs checking';
              const explanation =
                ignored ||
                outcomes[status] ||
                (status === 'failed'
                  ? messages[result?.error] ||
                    'An administrator needs to check this request before you send again.'
                  : ['unsent', 'invited', 'sending'].includes(status)
                    ? ''
                    : 'Could not confirm delivery. Ask an administrator to check.');
              return (
                <Table.Row key={`${index}-${raw}`}>
                  <Table.Cell className="flow-result-number">{number || raw}</Table.Cell>
                  <Table.Cell>
                    <div className="flow-result-outcome">
                      <Label
                        size="small"
                        color={
                          status === 'invited'
                            ? 'green'
                            : ['unsent', 'ignored'].includes(status)
                              ? undefined
                              : status === 'sending'
                                ? 'blue'
                                : 'orange'
                        }
                      >
                        {label}
                      </Label>
                      {explanation && <span className="flow-result-detail">{explanation}</span>}
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
      {error && (
        <p role="alert" className="validation-error">
          {error}
        </p>
      )}
      <div className="flow-actions" hidden={!entered.length}>
        <Button
          size="small"
          primary
          type="button"
          disabled={!ready || pending || !valid || (Boolean(requestId.current) && !canStartNew)}
          loading={busy}
          onClick={() => request('post')}
        >
          {canStartNew ? 'Send invitations again' : 'Send invitations'}
        </Button>
        <Button
          size="small"
          basic
          type="button"
          disabled={busy || !requestId.current}
          onClick={() => request('get')}
        >
          Check status
        </Button>
      </div>
    </section>
  );
  if (inline) return content;
  return (
    <>
      <Button type="button" primary disabled={!ready && !requestId.current} onClick={() => setOpen(true)}>
        {requestId.current ? 'View invitations' : 'Test on WhatsApp'}
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} size="small" closeOnDimmerClick={false}>
        <Modal.Header>Test on WhatsApp</Modal.Header>
        <Modal.Content>{content}</Modal.Content>
        <Modal.Actions>
          <Button type="button" onClick={() => setOpen(false)}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}
